import React, { useCallback, useReducer, useMemo, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Divider,
    Typography,
} from '@mui/material';
import { ValidDataService } from '../../../../service/validDataService';
import { isLocalDev } from '../../../../utils/environment';
import useAbortController from '../../../../hooks/useAbortController';
import SelectableTable from '../../../enhancedTable/SelectableTable';

export default function BrandListDialog({ open, onClose, onSelect, supplierId, type, initialFilter = '' }) {
    const [signal, abortRequest] = useAbortController();
    const [state, updateState] = useReducer((state, params) => {
        return { ...state, ...params };
    }, {
        data: [],
        totalRowCount: 0,
        pageCount: 0,
        filter: '',
        loading: false,
        initialFilterApplied: false,
    });

    // Apply initial filter when dialog opens
    useEffect(() => {
        if (open && initialFilter && !state.initialFilterApplied) {
            updateState({ filter: initialFilter, initialFilterApplied: true });
        }
        // Reset when dialog closes
        if (!open) {
            updateState({ filter: '', initialFilterApplied: false });
        }
    }, [open, initialFilter, state.initialFilterApplied]);

    const columns = useMemo(
        () => [
            {
                Header: 'Supplier Name',
                accessor: 'supplierName',
            },
            {
                Header: 'Name',
                accessor: 'name',
            },
            {
                Header: 'Type',
                accessor: 'type',
            },
            {
                Header: 'Actions',
                accessor: 'actions',
                Cell: ({ row }) => (
                    <Button
                        variant="text"
                        color="primary"
                        onClick={() => handleSelect(row.original)}
                    >
                        Select
                    </Button>
                ),
            },
        ],
        []
    );

    const fetchData = useCallback(({ pageSize, pageIndex, globalFilter, sortBy }) => {
        updateState({ loading: true });
        ValidDataService.getBrandForSupplierWithLimit(0, 1000, '', [], supplierId, signal).then(result => {
            const { code, data } = result;
            if (code === 0) {
                // Filter data client-side if there's a global filter
                let filteredData = data?.data || [];
                if (type) {
                    filteredData = filteredData.filter(item => item.type === type);
                }
                if (globalFilter) {
                    const lowerFilter = globalFilter.toLowerCase();
                    filteredData = filteredData.filter(item => 
                        item.name?.toLowerCase().includes(lowerFilter) ||
                        item.supplierName?.toLowerCase().includes(lowerFilter) ||
                        item.type?.toLowerCase().includes(lowerFilter)
                    );
                }
                
                // Sort data client-side if sortBy is provided
                if (sortBy && sortBy.length > 0) {
                    const { id, desc } = sortBy[0];
                    filteredData.sort((a, b) => {
                        const aVal = id.includes('.') 
                            ? id.split('.').reduce((obj, key) => obj?.[key], a)
                            : a[id];
                        const bVal = id.includes('.')
                            ? id.split('.').reduce((obj, key) => obj?.[key], b)
                            : b[id];
                        
                        if (aVal < bVal) return desc ? 1 : -1;
                        if (aVal > bVal) return desc ? -1 : 1;
                        return 0;
                    });
                }
                
                // Paginate client-side
                const from = pageSize * pageIndex;
                const paginatedData = filteredData.slice(from, from + pageSize);
                
                updateState({
                    data: paginatedData,
                    totalRowCount: filteredData.length,
                    pageCount: Math.ceil(filteredData.length / pageSize),
                });
            }
            updateState({ loading: false });
        }).catch(error => {
            if (isLocalDev) {
                console.error(error)
            }
            updateState({ loading: false });
        });
    }, [signal, supplierId, type]);

    const handleSelect = (supplier) => {
        onSelect(supplier);
        onClose();
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>Select Brand</DialogTitle>
            <DialogContent>
                <Box>
                    <Typography variant="h4">Brands</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <SelectableTable
                        columns={columns}
                        data={state.data}
                        totalRowCount={state.totalRowCount}
                        pageCount={state.pageCount}
                        fetchData={fetchData}
                        loading={state.loading}
                        disableSelection={true}
                        initialGlobalFilter={state.filter}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}